import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

/**
 * Form W-9 requests to contractors who crossed the IRS 1099-NEC threshold
 * ($600 paid in a tax year).
 *
 * The recipient list is deliberately NOT held in the frontend. It is contractor
 * names, personal email addresses and annual income, and CRA serves one main.js
 * to every visitor of the marketing site — so a hardcoded copy here would be
 * public. It is fetched per-request from an admin-only endpoint instead.
 */
class W9Service {
  authHeaders() {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Contractors who need a W-9 for the given tax year, plus any held back and
   * why (below threshold, or no email on file).
   *
   * @param {number} taxYear
   * @returns {Promise<{taxYear: number, threshold: number, hasData: boolean,
   *   availableTaxYears: number[], totalOnFile: number, totalTargeted: number,
   *   recipients: Array<{name: string, email: string, amount: number}>,
   *   skipped: Array<{name: string, email: string, amount: number, reason: string}>,
   *   skippedByReason: Object}>}
   */
  async getRecipients(taxYear) {
    const response = await axios.get(`${API_BASE_URL}/api/notifications/w9/recipients`, {
      headers: this.authHeaders(),
      params: { taxYear }
    });

    logger.log('[W9Service] Loaded recipients for', taxYear, response.data.data?.totalTargeted);
    return response.data.data;
  }

  /**
   * The email exactly as a contractor would receive it — nothing is sent.
   *
   * Pass the same subject/message the send will use, so the preview reflects any
   * edits. Omit `email` to render against the largest payment.
   *
   * @param {{taxYear: number, dueDate: string, email?: string, subject?: string,
   *   message?: string}} params
   * @returns {Promise<{subject: string, html: string, contractor: Object}>}
   */
  async previewEmail({ taxYear, dueDate, email, subject, message }) {
    const response = await axios.post(
      `${API_BASE_URL}/api/notifications/w9/preview-email`,
      { taxYear, dueDate, email, subject, message },
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );

    logger.log('[W9Service] Rendered preview for', response.data.data?.contractor?.email);
    return response.data.data;
  }

  /**
   * Send the campaign.
   *
   * `includeEmails` can only narrow the send — the backend intersects it with
   * the qualifying set, so an address that does not qualify is ignored rather
   * than emailed.
   *
   * @param {{taxYear: number, dueDate: string, includeEmails?: string[],
   *   subject?: string, message?: string}} params
   * @returns {Promise<{campaign: Object, results: {sent: Array, failed: Array, skipped: Array},
   *   campaignId: string|null, sentAt: string, sentBy: string|null}>}
   */
  async sendCampaign({ taxYear, dueDate, includeEmails, subject, message }) {
    const response = await axios.post(
      `${API_BASE_URL}/api/notifications/w9/send`,
      { taxYear, dueDate, includeEmails, subject, message },
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );

    const { data, sentBy } = response.data;
    logger.log('[W9Service] Campaign sent:', data.campaign);
    return { ...data, sentBy };
  }

  /**
   * Previous campaigns — the audit trail of who sent what, and when.
   *
   * @param {number} [limit]
   * @returns {Promise<Object[]>}
   */
  async getCampaigns(limit = 20) {
    const response = await axios.get(`${API_BASE_URL}/api/notifications/w9/campaigns`, {
      headers: this.authHeaders(),
      params: { limit }
    });
    return response.data.data.campaigns;
  }
}

export const w9Service = new W9Service();
export default w9Service;

/** Why a qualifying contractor could not be emailed. */
export const SKIP_REASON_LABELS = {
  no_email: 'No email on file',
  below_threshold: 'Below $600 threshold'
};
